import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Draft.css";
import API_URL from "./config";

const playerOverall = (p) => {
  const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const offense = avg([p.close, p.mid, p.three, p.free_throw, p.dunk, p.passing, p.dribbling, p.offensive_iq]);
  const defense = avg([p.defensive_iq, p.rebounding, p.strength, p.jumping]);
  const athletic = avg([p.speed, p.stamina, p.injury, p.jumping]);
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

const formatAttributeName = (name) => {
  const attributeMap = {
    close: "Close",
    mid: "Mid-Range",
    three: "Three-Point",
    free_throw: "Free Throw",
    dunk: "Dunking",
    passing: "Passing",
    dribbling: "Dribbling",
    offensive_iq: "Offensive IQ",
    defensive_iq: "Defensive IQ",
    rebounding: "Rebounding",
    strength: "Strength",
    jumping: "Jumping",
    speed: "Speed",
    stamina: "Stamina",
    injury: "Injury Rate",
  };
  return attributeMap[name] || name;
};

const Draft = ({ saveId, teamId, season }) => {
  const navigate = useNavigate();
  const [draftData, setDraftData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRookie, setSelectedRookie] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftedPlayers, setDraftedPlayers] = useState([]);
  const [draftStatus, setDraftStatus] = useState("not_started");
  const [isUserTurn, setIsUserTurn] = useState(false);
  const [draftComplete, setDraftComplete] = useState(false);
  const [teamRoster, setTeamRoster] = useState([]);
  const pollingRef = React.useRef(null);

  useEffect(() => {
    const fetchDraftBoard = async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = user._id;

        const res = await fetch(`${API_URL}/api/saves/${saveId}/draft`, {
          headers: { "X-User-ID": userId },
        });

        if (!res.ok) {
          throw new Error("Failed to load draft board");
        }

        const data = await res.json();
        setDraftData(data);
        setError(null);

        // Track drafted rookies
        const drafted = data.picks
          .filter((p) => p.drafted && p.rookieId)
          .map((p) => p.rookieId);
        setDraftedPlayers(drafted);

        // Fetch team roster
        const teamRes = await fetch(`${API_URL}/api/teams/${teamId}/roster?save_id=${saveId}`, {
          headers: { "X-User-ID": userId },
        });
        if (teamRes.ok) {
          const teamData = await teamRes.json();
          setTeamRoster(teamData.roster || []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (saveId) {
      fetchDraftBoard();
    }
  }, [saveId]);

  const handleDraftRookie = async (pickId, rookieId) => {
    // Prevent drafting if already in progress
    if (drafting) return;
    
    try {
      setDrafting(true);
      // Immediately clear selected rookie to show UI update happened
      setSelectedRookie(null);
      
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      const res = await fetch(`${API_URL}/api/saves/${saveId}/draft/select-rookie?pick_id=${pickId}&rookie_id=${rookieId}`, {
        method: "POST",
        headers: { "X-User-ID": userId },
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to draft rookie");
      }

      // Refresh draft board to get updated state (wait a bit for backend to sync)
      await new Promise(resolve => setTimeout(resolve, 300));
      await refreshDraftBoard(userId);
      setIsUserTurn(false);

      // Trigger next pick simulation
      setTimeout(() => pollForNextPick(userId), 500);
    } catch (err) {
      setError(err.message || "Error drafting rookie");
      setSelectedRookie(null);
    } finally {
      setDrafting(false);
    }
  };

  const refreshDraftBoard = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/saves/${saveId}/draft`, {
        headers: { "X-User-ID": userId },
      });
      if (res.ok) {
        const data = await res.json();
        setDraftData(data);
        // Update draft status from backend
        if (data.draftStatus) {
          setDraftStatus(data.draftStatus);
          if (data.draftStatus === "completed") {
            setDraftComplete(true);
          }
        }
        const drafted = data.picks
          .filter((p) => p.drafted && p.rookieId)
          .map((p) => p.rookieId);
        setDraftedPlayers(drafted);
      }
    } catch (err) {
      // Error refreshing draft board
    }
  };

  const pollForNextPick = async (userId) => {
    try {
      // Don't poll if draft is already complete
      if (draftComplete) {
        return;
      }

      const res = await fetch(`${API_URL}/api/saves/${saveId}/draft/simulate-next-pick`, {
        method: "POST",
        headers: { "X-User-ID": userId },
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail || "Error simulating next pick";
        // If draft is complete, don't show error - just silently return
        if (detail.includes("Draft has not been started") || detail.includes("already been")) {
          // Refresh to get the actual status
          await refreshDraftBoard(userId);
          return;
        }
        setError(detail);
        return;
      }

      const result = await res.json();

      if (result.draftComplete) {
        setDraftComplete(true);
        setDraftStatus("completed");
        await refreshDraftBoard(userId);
        return;
      }

      if (result.isUserTurn) {
        setIsUserTurn(true);
        await refreshDraftBoard(userId);
        return;
      }

      // A non-user pick was simulated, refresh and continue polling
      await refreshDraftBoard(userId);
      // Schedule next poll immediately
      setTimeout(() => pollForNextPick(userId), 500);
    } catch (err) {
      setError(err.message || "Error polling for next pick");
      // Poll error
    }
  };

  useEffect(() => {
    if (draftStatus === "in_progress" && !isUserTurn && !draftComplete) {
      pollingRef.current = setTimeout(() => {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        pollForNextPick(user._id);
      }, 1000);

      return () => {
        if (pollingRef.current) clearTimeout(pollingRef.current);
      };
    }
  }, [draftStatus, isUserTurn, draftComplete]);

  const handleStartDraft = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      const res = await fetch(`${API_URL}/api/saves/${saveId}/draft/start`, {
        method: "POST",
        headers: { "X-User-ID": userId },
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to start draft");
      }

      setDraftStatus("in_progress");
      // Start polling for next picks
      setTimeout(() => pollForNextPick(userId), 500);
    } catch (err) {
      setError(err.message || "Error starting draft");
      // Draft start error
    }
  };

  if (loading) {
    return (
      <div className="draft-container">
        <div className="draft-loading">Loading draft board...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="draft-container">
        <div className="draft-error">Error: {error}</div>
      </div>
    );
  }

  if (!draftData) {
    return (
      <div className="draft-container">
        <div className="draft-empty">Draft board not available</div>
      </div>
    );
  }

  const userTeamPicks = draftData.picks
    .filter((p) => p.teamId === teamId)
    .sort((a, b) => a.round - b.round || a.pickNumber - b.pickNumber);

  const allPicks = draftData.picks.sort((a, b) => a.round - b.round || a.pickNumber - b.pickNumber);

  // Sort rookies by starRating descending, then by topFour_avg descending
  const availableRookies = draftData.rookies
    .filter((r) => !draftedPlayers.includes(r._id))
    .sort((a, b) => {
      // First sort by stars descending
      const starDiff = (b.starRating || 0) - (a.starRating || 0);
      if (starDiff !== 0) return starDiff;
      // Then by topFour_avg descending
      return (b.topFour_avg || 0) - (a.topFour_avg || 0);
    });

  const round1Picks = allPicks.filter((p) => p.round === 1);
  const round2Picks = allPicks.filter((p) => p.round === 2);

  return (
    <div className="draft-container">
      <div className="draft-header">
        <h2>Season {draftData.season} Draft Board</h2>
        <div className="draft-header-right">
          {draftComplete ? (
            <div className="draft-header-complete">
              <div className="draft-status complete">✓ Draft Complete!</div>
              <button 
                className="back-to-dashboard-btn"
                onClick={() => {
                  // Advance to free agency phase and return to dashboard
                  try {
                    const activeSaveId = localStorage.getItem("activeSaveId");
                    if (activeSaveId) {
                      const currentPhase = parseInt(localStorage.getItem(`current_phase_${activeSaveId}`) || "1", 10);
                      const nextPhase = currentPhase + 1;
                      localStorage.setItem(`current_phase_${activeSaveId}`, nextPhase.toString());
                    }
                  } catch (err) {
                    // Error advancing phase
                  }
                  navigate('/dashboard');
                }}
              >
                Proceed to Free Agency
              </button>
            </div>
          ) : draftStatus === "not_started" ? (
            <button 
              className="start-draft-btn" 
              onClick={handleStartDraft}
            >
              Start Draft
            </button>
          ) : isUserTurn ? (
            <div className="draft-status user-turn">🎯 Your Turn to Pick!</div>
          ) : (
            <div className="draft-status in-progress">⏳ AI Teams Drafting...</div>
          )}
        </div>
      </div>

      <div className="draft-main">
        {/* Left: Available Rookies */}
        <div className="draft-rookies">
          <div className="draft-rookies-header">
            <h3>Available Rookies ({availableRookies.length})</h3>
          </div>
          <div className="draft-rookies-list">
            {availableRookies.map((rookie, index) => {
              const rank = index + 1;
              const userPick = userTeamPicks.find(p => !p.drafted);
              return (
                <div
                  key={rookie._id}
                  className={`draft-rookie-card ${selectedRookie?.rookie?._id === rookie._id ? "selected" : ""}`}
                  onClick={() => {
                    if (!isUserTurn) return;
                    setSelectedRookie({ rookie, pick: userPick });
                  }}
                >
                  <div className="rookie-card-top">
                    <span className="rookie-rank">#{rank}</span>
                    <span className="rookie-name">{rookie.name}</span>
                    <span className="rookie-star-rating">
                      {"★".repeat(rookie.starRating || 0)}{"☆".repeat(5 - (rookie.starRating || 0))}
                    </span>
                  </div>
                  <div className="rookie-card-meta">
                    <span className="rookie-pos">{rookie.position}</span>
                    <span className="rookie-size">
                      {Math.floor(rookie.height / 12)}'
                      {rookie.height % 12}"
                    </span>
                  </div>
                  <div className="rookie-card-stats">
                    {rookie.topAttributes && rookie.topAttributes.map((attr) => (
                      <span key={attr.name} className="stat-mini">
                        {formatAttributeName(attr.name)}: {attr.value}
                      </span>
                    ))}
                  </div>
                  {isUserTurn && userPick && (
                    <button
                      className="draft-rookie-btn"
                      disabled={drafting}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDraftRookie(userPick._id, rookie._id);
                      }}
                    >
                      {drafting ? "Drafting..." : "Draft"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Picks and Board */}
        <div className="draft-board-section">
          <div className="draft-your-picks">
            <h3>Your Picks</h3>
            <div className="your-picks-list">
              {userTeamPicks.length === 0 ? (
                <p className="no-picks">No picks available</p>
              ) : (
                userTeamPicks.map((pick) => {
                  const draftedRookie = draftData.rookies.find((r) => r._id === pick.rookieId);
                  return (
                    <div
                      key={pick._id}
                      className={`your-pick-row ${pick.drafted ? "drafted" : "available"} ${
                        selectedRookie?.pick?._id === pick._id ? "active" : ""
                      }`}
                      onClick={() => {
                        if (!pick.drafted) {
                          setSelectedRookie({ pick, rookie: null });
                        }
                      }}
                    >
                      <span className="pick-label">
                        Round {pick.round} · Pick {pick.pickNumber}
                      </span>
                      {draftedRookie ? (
                        <span className="pick-player">{draftedRookie.name}</span>
                      ) : (
                        <span className="pick-empty">Select Rookie</span>
                      )}
                      {selectedRookie?.rookie && selectedRookie?.pick?._id === pick._id && !pick.drafted && (
                        <button
                          className="pick-confirm-btn"
                          disabled={drafting}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDraftRookie(pick._id, selectedRookie.rookie._id);
                          }}
                        >
                          {drafting ? "Drafting..." : "Confirm Draft"}
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Full Draft Board */}
          <div className="draft-full-board">
            <div className="draft-round">
              <h4>Round 1</h4>
              <div className="draft-picks-grid">
                {round1Picks.map((pick) => {
                  const draftedRookie = draftData.rookies.find((r) => r._id === pick.rookieId);
                  const isUserTeam = pick.teamId === teamId;
                  return (
                    <div
                      key={pick._id}
                      className={`draft-pick-badge ${pick.drafted ? "drafted" : ""} ${isUserTeam ? "user-team" : ""}`}
                      title={`${pick.pickNumber}. ${pick.teamName}${draftedRookie ? ` - ${draftedRookie.name}` : ""}`}
                    >
                      <span className="pick-num">{pick.pickNumber}</span>
                      <span className="pick-team">{pick.teamName}</span>
                      {draftedRookie && <span className="pick-name">{draftedRookie.name}</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="draft-round">
              <h4>Round 2</h4>
              <div className="draft-picks-grid">
                {round2Picks.map((pick) => {
                  const draftedRookie = draftData.rookies.find((r) => r._id === pick.rookieId);
                  const isUserTeam = pick.teamId === teamId;
                  return (
                    <div
                      key={pick._id}
                      className={`draft-pick-badge ${pick.drafted ? "drafted" : ""} ${isUserTeam ? "user-team" : ""}`}
                      title={`${pick.pickNumber}. ${pick.teamName}${draftedRookie ? ` - ${draftedRookie.name}` : ""}`}
                    >
                      <span className="pick-num">{pick.pickNumber}</span>
                      <span className="pick-team">{pick.teamName}</span>
                      {draftedRookie && <span className="pick-name">{draftedRookie.name}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Your Roster Card */}
      <div className="draft-roster-card">
        <h3>Your Roster by Position</h3>
        {teamRoster.length === 0 ? (
          <p className="no-roster">No players on roster yet</p>
        ) : (
          <div className="roster-by-position">
            {["PG", "SG", "SF", "PF", "C"].map((position) => {
              const playersAtPosition = teamRoster.filter(p => p.position === position);
              return (
                <div key={position} className="position-group">
                  <div className="position-header">{position}</div>
                  <div className="position-players">
                    {playersAtPosition.length === 0 ? (
                      <span className="no-player">—</span>
                    ) : (
                      playersAtPosition.map(p => (
                        <div key={p._id} className="roster-player">
                          <span className="player-name">{p.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Draft;
